import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Copy, Plus, Loader2, UserCheck, Building2, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { useBooking } from '../contexts/BookingContext';
import { useAuth } from '../contexts/AuthContext';
import { useCustomer } from '../contexts/CustomerContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { formatTime, toMinAnchored } from '../utils/timeSlots';

const BulkBooking = () => {
  const { addBooking } = useBooking();
  const { facility, user } = useAuth();
  const { getCustomerByPhone } = useCustomer();
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [failureSummary, setFailureSummary] = useState(null); // {successCount, failCount, failures:[{date,reason}], buckets}
  const [customerFound, setCustomerFound] = useState(false);
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);
  
  // Check if this is SUFC facility
  const isSUFC = facility?.id === 'facility_trp_sports' || 
                 facility?.name?.toLowerCase()?.includes('south united') ||
                 user?.facility_id === 'facility_trp_sports';

  // Check if this is Sports Web - BCFC facility
  const isBCFC = facility?.id === 'facility_sportsweb' ||
                 facility?.name?.toLowerCase()?.includes('sportsweb') ||
                 facility?.name?.toLowerCase()?.includes('sports web') ||
                 facility?.name?.toLowerCase()?.includes('bcfc') ||
                 user?.facility_id === 'facility_sportsweb';
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    startDate: '',
    startTime: '',
    endTime: '',
    courtId: '',
    paymentStatus: 'pending',
    amountPaid: 0,
    recurrenceType: 'weekly',
    weeksCount: 1,
    endDate: '',
    useEndDate: false,
    selectedDays: [],
    bookingType: 'b2c',  // b2c, b2b, or event
    customRate: 0,       // Per hour rate for B2B/Event
    customRateTouched: false, // false = auto-fill standard; true = admin explicitly typed (incl. explicit 0)
    sendEmailConfirmation: false,  // Send individual emails (legacy)
    sendSummaryEmail: true  // Send single consolidated summary email (default ON)
  });
  // Map of ISO date → price for that specific date (backend-computed).
  // Populated by the useEffect below whenever the pricing inputs change.
  const [datePrices, setDatePrices] = useState({});
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState('');

  // Generate time slots based on facility operating hours
  useEffect(() => {
    if (facility?.operating_hours) {
      const [startHour, startMinute] = facility.operating_hours.start.split(':').map(Number);
      const [endHour, endMinute] = facility.operating_hours.end.split(':').map(Number);
      const slots = [];
      const startTotal = startHour * 60 + startMinute;
      const endTotal = endHour * 60 + endMinute;

      const emit = (from, to) => {
        for (let t = from; t + 30 <= to; t += 30) {
          const h = Math.floor(t / 60) % 24;
          const m = t % 60;
          slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
        }
      };

      if (endTotal <= startTotal) {
        // Overnight (e.g. 05:00 to 02:00): early morning then evening
        emit(0, endTotal);
        emit(startTotal, 24 * 60);
      } else {
        emit(startTotal, endTotal);
      }

      setTimeSlots(slots);
      // Set default court
      if (facility.courts?.length > 0 && !formData.courtId) {
        setFormData(prev => ({...prev, courtId: facility.courts[0].id}));
      }
      setLoading(false);
    } else {
      // Fallback: generate default time slots (6 AM to 11 PM)
      const slots = [];
      for (let hour = 6; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
      }
      setTimeSlots(slots);
      setLoading(false);
    }
  }, [facility]);

  // Customer phone lookup
  useEffect(() => {
    const lookupCustomer = async () => {
      if (formData.customerPhone && formData.customerPhone.length === 10) {
        setLookingUpCustomer(true);
        try {
          const customer = await getCustomerByPhone(formData.customerPhone);
          if (customer) {
            setFormData(prev => ({
              ...prev,
              customerName: customer.name || prev.customerName,
              customerEmail: customer.email || prev.customerEmail
            }));
            setCustomerFound(true);
            toast({
              title: "Customer Found",
              description: `Loaded details for ${customer.name}.`,
            });
          } else {
            setCustomerFound(false);
          }
        } catch (error) {
          setCustomerFound(false);
        } finally {
          setLookingUpCustomer(false);
        }
      } else {
        setCustomerFound(false);
      }
    };
    
    const timeoutId = setTimeout(lookupCustomer, 300);
    return () => clearTimeout(timeoutId);
  }, [formData.customerPhone, getCustomerByPhone]);

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const handleDayToggle = (day) => {
    if (formData.selectedDays.includes(day)) {
      setFormData({
        ...formData,
        selectedDays: formData.selectedDays.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        selectedDays: [...formData.selectedDays, day]
      });
    }
  };

  const calculateTotalSlots = () => {
    // Anchored-minute compare — handles cross-midnight ranges (e.g. 23:00 →
    // 00:30) the same way the customer booking grid does. Both this
    // helper and the grid now share ONE `toMinAnchored` from
    // `utils/timeSlots.js` so there's no way for bulk and single-booking
    // to drift.
    if (!formData.startTime || !formData.endTime) return 0;
    const startM = toMinAnchored(formData.startTime, formData.startTime);
    const endM = toMinAnchored(formData.endTime, formData.startTime);
    const diff = endM - startM;
    if (diff <= 0) return 0;
    return diff / 30;
  };

  // True when the selected range rolls past midnight — used for the small
  // "next day" hint in the summary.
  const isOvernight = () => {
    if (!formData.startTime || !formData.endTime) return false;
    return toMinAnchored(formData.endTime, formData.startTime) > 1440;
  };

  // Per-date pricing via backend `/slots/price` — single source of truth
  // shared with the calendar / grid so peak/off-peak/weekday/weekend cannot
  // diverge between bulk and single-booking flows.
  //
  // For B2B/Event/SUFC/BCFC we fetch the STANDARD rate as the default so we
  // never silently record ₹0 for those types. If admin has explicitly typed
  // a customRate (customRateTouched=true) — including an explicit 0 comp —
  // that override applies uniformly across every date.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const dates = getDatesForRecurrence();
      const slots = calculateTotalSlots();
      const court = facility?.courts?.find(c => c.id === formData.courtId);
      const slotType = court?.court_type || court?.sport || 'full';
      if (!formData.startTime || !formData.endTime || slots <= 0 || dates.length === 0 || !court) {
        setDatePrices({});
        setPricingError('');
        return;
      }
      // Comp types (sufc/bcfc) default to ₹0; paid override types (b2b/event)
      // default to the standard ground rate so we don't silently charge nothing.
      const COMP_TYPES = ['sufc', 'bcfc'];
      const PAID_OVERRIDE_TYPES = ['b2b', 'event'];
      const isOverride = PAID_OVERRIDE_TYPES.includes(formData.bookingType)
                         || COMP_TYPES.includes(formData.bookingType);
      const hours = slots * 0.5;
      // Explicit admin override → flat per-slot price uniformly across dates.
      if (isOverride && formData.customRateTouched) {
        const flat = (formData.customRate || 0) * hours;
        const next = {};
        for (const d of dates) next[d] = flat;
        setDatePrices(next);
        setPricingError('');
        return;
      }
      // Comp types untouched → ₹0 across all dates. No backend call needed.
      if (COMP_TYPES.includes(formData.bookingType) && !formData.customRateTouched) {
        const next = {};
        for (const d of dates) next[d] = 0;
        setDatePrices(next);
        setPricingError('');
        return;
      }
      // Otherwise, fetch the canonical price per date from the backend so
      // weekend/weekday and peak/off-peak resolve slot-by-slot correctly.
      setPricingLoading(true);
      setPricingError('');
      try {
        const results = await Promise.all(dates.map(async (d) => {
          const res = await api.calculatePrice({
            slot_type: slotType,
            start_time: formData.startTime,
            end_time: formData.endTime,
            date: d,
            booking_type: 'b2c', // always ask for standard rate — overrides handled above
          });
          return [d, Number(res?.total || 0)];
        }));
        if (cancelled) return;
        const map = Object.fromEntries(results);
        setDatePrices(map);
        // Only pre-fill the rate field for PAID override types (b2b/event).
        // Comp types already returned above with ₹0.
        if (PAID_OVERRIDE_TYPES.includes(formData.bookingType)
            && !formData.customRateTouched && hours > 0) {
          const avg = Object.values(map).reduce((s, v) => s + v, 0) / Math.max(1, Object.values(map).length);
          const stdHourly = avg / hours;
          setFormData(prev => ({ ...prev, customRate: Math.round(stdHourly * 100) / 100 }));
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Bulk pricing fetch failed:', e);
          setPricingError('Could not load rates for these dates. Please retry or set a manual rate.');
          setDatePrices({});
        }
      } finally {
        // Always reset the loading flag — even on a cancelled run — otherwise
        // a user who switches booking type (or anything else that triggers a
        // sync short-circuit in the next effect run) while this fetch is
        // in-flight gets stuck with `pricingLoading=true` forever, blocking
        // the submit guard introduced in handleSubmit.
        setPricingLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [
    formData.startDate, formData.endDate, formData.useEndDate, formData.weeksCount,
    formData.recurrenceType, formData.selectedDays.join(','),
    formData.startTime, formData.endTime, formData.courtId, formData.bookingType,
    formData.customRate, formData.customRateTouched, facility?.id,
  ]);

  // Sum of all per-date prices — used for the summary UI.
  const totalBookingAmount = () => {
    const dates = getDatesForRecurrence();
    return dates.reduce((sum, d) => sum + (datePrices[d] || 0), 0);
  };

  // Representative first-date price for the "Price per booking" line in the
  // summary. When peak/off-peak differs across dates we show a range instead
  // so the admin can see the split without being surprised.
  const priceSummary = () => {
    const dates = getDatesForRecurrence();
    if (dates.length === 0) return { display: '₹0', min: 0, max: 0, mixed: false };
    const values = dates.map(d => datePrices[d] || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const mixed = min !== max;
    const display = mixed ? `₹${min} – ₹${max}` : `₹${min}`;
    return { display, min, max, mixed };
  };

  const getDatesForRecurrence = () => {
    const dates = [];
    if (!formData.startDate) return dates;
    
    // Parse date in local timezone correctly (avoid UTC conversion issues)
    const [year, month, day] = formData.startDate.split('-').map(Number);
    const startDate = new Date(year, month - 1, day); // month is 0-indexed
    
    const endDate = formData.useEndDate && formData.endDate 
      ? (() => {
          const [ey, em, ed] = formData.endDate.split('-').map(Number);
          return new Date(ey, em - 1, ed);
        })()
      : null;
    
    const weeksCount = formData.useEndDate && endDate
      ? Math.ceil((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)) + 1
      : formData.weeksCount;
    
    // Helper to format date as YYYY-MM-DD
    const formatDate = (d) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    
    if (formData.recurrenceType === 'weekly') {
      for (let week = 0; week < weeksCount; week++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + (week * 7));
        if (!endDate || date <= endDate) {
          dates.push(formatDate(date));
        }
      }
    } else {
      const startDay = startDate.getDay(); // 0=Sunday, 1=Monday, etc.
      const dayMap = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      
      for (let week = 0; week < weeksCount; week++) {
        formData.selectedDays.forEach(dayName => {
          const targetDay = dayMap[dayName];
          const date = new Date(startDate);
          
          // Calculate days to add to reach the target day
          let daysToAdd = targetDay - startDay;
          if (daysToAdd < 0) daysToAdd += 7; // Move to next week if target is before start
          
          // Add weeks offset
          daysToAdd += (week * 7);
          
          date.setDate(startDate.getDate() + daysToAdd);
          
          if (date >= startDate && (!endDate || date <= endDate)) {
            dates.push(formatDate(date));
          }
        });
      }
    }
    
    // Remove duplicates and sort
    return [...new Set(dates)].sort();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.startTime || !formData.endTime) {
      toast({
        title: "Validation Error",
        description: "Please select both start and end times.",
        variant: "destructive"
      });
      return;
    }

    if (formData.recurrenceType === 'specific-days' && formData.selectedDays.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one day of the week.",
        variant: "destructive"
      });
      return;
    }

    const dates = getDatesForRecurrence();
    if (dates.length === 0) {
      toast({
        title: "Validation Error",
        description: "No dates generated for the recurrence pattern.",
        variant: "destructive"
      });
      return;
    }

    const court = facility.courts?.find(c => c.id === formData.courtId);
    const slotType = court?.court_type || court?.sport || 'full';

    // Any override-type booking (b2b/event/sufc/bcfc) with an explicit admin
    // rate must carry `custom_rate` to the backend so the audit trail records
    // it as an override rather than a standard-pricing booking.
    const isOverride = ['b2b', 'event', 'sufc', 'bcfc'].includes(formData.bookingType);

    // Safety guard: for PAID override types (b2b/event) without an
    // explicit admin override, refuse to submit if any date resolved to ₹0.
    // Comp types (sufc/bcfc) are ₹0 by design — no guard.
    const PAID_OVERRIDE_BULK = ['b2b', 'event'];
    const anyZero = dates.some(d => (datePrices[d] || 0) <= 0);
    if (PAID_OVERRIDE_BULK.includes(formData.bookingType)
        && anyZero && !formData.customRateTouched) {
      toast({
        title: 'Rate not set',
        description: 'Standard rate could not be applied to every date. Enter a rate in ₹/hour (use 0 only for an explicit comp).',
        variant: 'destructive',
      });
      return;
    }
    if (pricingError) {
      toast({ title: 'Pricing error', description: pricingError, variant: 'destructive' });
      return;
    }
    // Block submit while rates are still being fetched — otherwise the
    // bookings would be created against stale `datePrices` from a previous
    // court/time/date combination and silently record the wrong amount.
    if (pricingLoading) {
      toast({ title: 'Rates loading', description: 'Please wait for the per-date rates to finish loading before creating the bookings.', variant: 'destructive' });
      return;
    }
    // Every date in the recurrence must have a resolved price before we
    // submit. A missing entry means `datePrices` is stale (useEffect hasn't
    // caught up with the latest inputs yet) and submitting now would record
    // the wrong amount for that date.
    const missingDates = dates.filter(d => datePrices[d] === undefined);
    if (missingDates.length > 0) {
      toast({ title: 'Rates not ready', description: `Rate not yet available for ${missingDates.length} date(s). Please wait a moment and try again.`, variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    let failCount = 0;
    const failures = [];
    const createdBookingIds = []; // Track created booking IDs for summary email

    for (const date of dates) {
      const pricePerSlot = datePrices[date] || 0;
      const amountPaid = formData.paymentStatus === 'paid' ? pricePerSlot :
                         formData.paymentStatus === 'partial' ? formData.amountPaid :
                         0;
      const bookingData = {
        facility_id: facility.id,
        date: date,
        start_time: formData.startTime,
        end_time: formData.endTime,
        slot_type: slotType,
        section: formData.courtId,
        court_id: formData.courtId,
        sport: court?.sport || 'football',
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        status: 'confirmed',
        amount: pricePerSlot,
        amount_paid: amountPaid,
        payment_status: formData.paymentStatus,
        source: 'direct',
        booking_type: formData.bookingType,
        custom_rate: (isOverride && formData.customRateTouched) ? formData.customRate : null,
        notes: `Created via bulk booking (${formData.bookingType.toUpperCase()})`,
        send_confirmation_email: formData.sendEmailConfirmation, // Individual emails (usually OFF)
        skip_push_notification: true // Skip individual push notifications for bulk bookings
      };

      try {
        const result = await addBooking(bookingData);
        successCount++;
        if (result?.id) {
          createdBookingIds.push(result.id);
        }
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (err) {
        failCount++;
        failures.push({ date, reason: err.message || 'Unknown error' });
      }
    }

    // Send consolidated summary email if enabled and bookings were created
    if (formData.sendSummaryEmail && createdBookingIds.length > 0 && formData.customerEmail) {
      try {
        await api.sendBulkBookingSummaryEmail({
          booking_ids: createdBookingIds,
          facility_id: facility.id,
          customer_email: formData.customerEmail,
          customer_name: formData.customerName,
          court_name: court?.name || formData.courtId
        });
        toast({
          title: "Summary Email Sent",
          description: `Booking summary sent to ${formData.customerEmail}`,
        });
      } catch (emailErr) {
        console.error('Failed to send summary email:', emailErr);
        toast({
          title: "Email Failed",
          description: "Bookings created but summary email failed to send.",
          variant: "destructive"
        });
      }
    }

    // Send ONE consolidated push notification for all bulk bookings
    if (successCount > 0) {
      try {
        // Format date range for notification
        const sortedDates = getDatesForRecurrence().sort();
        const firstDate = new Date(sortedDates[0]).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
        const lastDate = new Date(sortedDates[sortedDates.length - 1]).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
        const dateRange = sortedDates.length === 1 ? firstDate : `${firstDate} - ${lastDate}`;
        
        await api.sendBulkBookingPushNotification({
          booking_count: successCount,
          facility_id: facility.id,
          customer_name: formData.customerName,
          court_name: court?.name || formData.courtId,
          total_amount: totalBookingAmount(),
          date_range: dateRange
        });
      } catch (notifyErr) {
        console.error('Failed to send bulk push notification:', notifyErr);
        // Silent fail for push notifications - not critical
      }
    }

    setSubmitting(false);

    const resetForm = () => {
      setFormData(prev => ({
        ...prev,
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        startDate: '',
        startTime: '',
        endTime: '',
        paymentStatus: 'pending',
        amountPaid: 0,
        weeksCount: 1,
        endDate: '',
        useEndDate: false,
        bookingType: 'b2c',
        customRate: 0,
        customRateTouched: false,
        selectedDays: [],
        sendEmailConfirmation: false,
        sendSummaryEmail: true
      }));
      setCustomerFound(false);
    };

    if (failCount === 0 && successCount > 0) {
      // All booked — success toast only, no dialog
      toast({
        title: "Bulk Booking Created",
        description: `${successCount} booking${successCount > 1 ? 's' : ''} created successfully.`,
      });
      resetForm();
    } else if (successCount === 0) {
      // Nothing booked — error toast only, no dialog
      toast({
        title: "Bulk Booking Failed",
        description: failures.length
          ? `No slots could be booked. ${categorizeFailures(failures).summary}`
          : "No slots could be booked.",
        variant: "destructive",
      });
    } else {
      // Mixed — show the summary dialog with each failed date + reason
      resetForm();
      setFailureSummary({
        successCount,
        failCount,
        failures,
        buckets: categorizeFailures(failures),
      });
    }
  };

  // Group failure reasons into human buckets for an inline summary line.
  const categorizeFailures = (failures) => {
    const counts = { alreadyBooked: 0, outsideHours: 0, other: 0 };
    failures.forEach(f => {
      const r = (f.reason || '').toLowerCase();
      if (/already|conflict|booked|overlap|exists|not available|unavailable|taken/.test(r)) counts.alreadyBooked++;
      else if (/hour|operating|outside|closed/.test(r)) counts.outsideHours++;
      else counts.other++;
    });
    const parts = [];
    if (counts.alreadyBooked) parts.push(`${counts.alreadyBooked} already booked`);
    if (counts.outsideHours) parts.push(`${counts.outsideHours} outside hours`);
    if (counts.other) parts.push(`${counts.other} other`);
    return { ...counts, summary: parts.join(', ') };
  };

  const copyFailureList = () => {
    if (!failureSummary) return;
    const text = failureSummary.failures.map(f => `${f.date}: ${f.reason}`).join('\n');
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "Failed slots copied to clipboard." });
  };

  if (loading || !facility) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Loading booking form...</span>
      </div>
    );
  }

  return (
    <div className="p-6 pt-14 lg:pt-6 pb-32 lg:pb-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bulk Booking</h1>
          <p className="text-gray-500">Create recurring bookings for multiple weeks</p>
        </div>
        <div className="flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-lg">
          <Building2 className="h-5 w-5 text-gray-600" />
          <span className="font-semibold">{facility.name}</span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Copy className="mr-2 h-5 w-5" />
            Recurring Booking Form
          </CardTitle>
          <CardDescription>Book the same slot for multiple days/weeks</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Customer Details</h3>
              <p className="text-sm text-blue-600">💡 Enter phone number first - customer details will auto-populate if found</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customerPhone">Phone (10 digits)</Label>
                  <div className="relative">
                    <Input
                      id="customerPhone"
                      type="tel"
                      required
                      pattern="[0-9]{10}"
                      maxLength="10"
                      value={formData.customerPhone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setFormData({ ...formData, customerPhone: value });
                      }}
                      placeholder="9876543210"
                      className={customerFound ? 'border-green-500 pr-10' : ''}
                    />
                    {lookingUpCustomer && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      </div>
                    )}
                    {customerFound && !lookingUpCustomer && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <UserCheck className="h-4 w-4 text-green-500" />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="customerName">Customer Name</Label>
                  <Input
                    id="customerName"
                    required
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    placeholder="Enter customer name"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="customerEmail">Email</Label>
                  <Input
                    id="customerEmail"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                    placeholder="customer@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Slot Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Slot Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="courtId">Court / Field</Label>
                  <select
                    id="courtId"
                    className="w-full p-2 border rounded"
                    value={formData.courtId}
                    onChange={(e) => setFormData({ ...formData, courtId: e.target.value })}
                  >
                    {facility.courts?.map(court => (
                      <option key={court.id} value={court.id}>
                        {court.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="bulk-start-date"
                    className="cursor-pointer"
                  />
                </div>
                <div>
                  <Label htmlFor="startTime">Start Time</Label>
                  <select
                    id="startTime"
                    className="w-full p-2 border rounded"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    required
                  >
                    <option value="">Select start time</option>
                    {timeSlots.map((time) => (
                      <option key={time} value={time}>{formatTime(time)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="endTime">End Time</Label>
                  <select
                    id="endTime"
                    className="w-full p-2 border rounded"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    required
                  >
                    <option value="">Select end time</option>
                    {timeSlots.map((time) => (
                      <option key={time} value={time}>{formatTime(time)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Recurrence Pattern */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Recurrence Pattern</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="recurrenceType">Recurrence Type</Label>
                  <select
                    id="recurrenceType"
                    className="w-full p-2 border rounded"
                    value={formData.recurrenceType}
                    onChange={(e) => setFormData({ ...formData, recurrenceType: e.target.value, selectedDays: [] })}
                  >
                    <option value="weekly">Same day each week</option>
                    <option value="specific-days">Specific days of week</option>
                  </select>
                </div>
                <div>
                  <Label>Duration Method</Label>
                  <div className="flex items-center space-x-4 mt-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={!formData.useEndDate}
                        onChange={() => setFormData({ ...formData, useEndDate: false })}
                        className="mr-2"
                      />
                      <span className="text-sm">Weeks Count</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={formData.useEndDate}
                        onChange={() => setFormData({ ...formData, useEndDate: true })}
                        className="mr-2"
                      />
                      <span className="text-sm">End Date</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {!formData.useEndDate ? (
                  <div>
                    <Label htmlFor="weeksCount">Number of Weeks</Label>
                    <Input
                      id="weeksCount"
                      type="number"
                      min="1"
                      max="52"
                      required
                      value={formData.weeksCount}
                      onChange={(e) => setFormData({ ...formData, weeksCount: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      required
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      min={formData.startDate}
                      data-testid="bulk-end-date"
                      className="cursor-pointer"
                    />
                  </div>
                )}
              </div>

              {formData.recurrenceType === 'specific-days' && (
                <div>
                  <Label>Select Days of Week</Label>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mt-2">
                    {daysOfWeek.map((day) => (
                      <Button
                        key={day.value}
                        type="button"
                        variant={formData.selectedDays.includes(day.value) ? 'default' : 'outline'}
                        onClick={() => handleDayToggle(day.value)}
                        className="text-xs"
                      >
                        {day.label.slice(0, 3)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Booking Type */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Booking Type</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bookingType">Type</Label>
                  <select
                    id="bookingType"
                    className="w-full p-2 border rounded"
                    value={formData.bookingType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      // Reset the "touched" flag when booking type changes so
                      // the standard-rate auto-prime kicks in again.
                      setFormData({
                        ...formData,
                        bookingType: newType,
                        customRate: 0,
                        customRateTouched: false,
                      });
                    }}
                    data-testid="bulk-booking-type"
                  >
                    <option value="b2c">B2C (Regular Customer)</option>
                    <option value="b2b">B2B (Corporate/Business)</option>
                    <option value="event">Event</option>
                    {isSUFC && (
                      <option value="sufc">SUFC (Internal)</option>
                    )}
                    {isBCFC && (
                      <option value="bcfc">BCFC (Internal)</option>
                    )}
                  </select>
                  {['sufc', 'bcfc'].includes(formData.bookingType) && !formData.customRateTouched && (
                    <p className="text-xs text-slate-600 mt-1">
                      Internal — default ₹0 across all dates. Enter a rate below to charge instead.
                    </p>
                  )}
                </div>
                {['b2b', 'event', 'sufc', 'bcfc'].includes(formData.bookingType) && (
                  <div>
                    <Label htmlFor="customRate">
                      Rate (₹ per hour)
                      {!formData.customRateTouched && ['b2b', 'event'].includes(formData.bookingType)
                        && <span className="text-[10px] font-normal text-gray-500 ml-1">(standard rate — edit to override)</span>}
                      {!formData.customRateTouched && ['sufc', 'bcfc'].includes(formData.bookingType)
                        && <span className="text-[10px] font-normal text-gray-500 ml-1">(default ₹0 — edit to charge)</span>}
                    </Label>
                    <Input
                      id="customRate"
                      type="number"
                      min="0"
                      value={formData.customRate}
                      onChange={(e) => setFormData({ ...formData, customRate: parseFloat(e.target.value) || 0, customRateTouched: true })}
                      placeholder="Enter hourly rate"
                      data-testid="bulk-custom-rate"
                    />
                    {formData.customRateTouched && formData.customRate === 0 && (
                      <p className="text-xs text-amber-700 mt-1" data-testid="bulk-explicit-comp-note">
                        ⚠ Explicit ₹0 comp — every booking in this batch will be recorded at ₹0.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {['b2b', 'event', 'sufc', 'bcfc'].includes(formData.bookingType) && formData.customRateTouched && formData.customRate > 0 && calculateTotalSlots() > 0 && (
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Custom Rate:</strong> ₹{formData.customRate}/hour × {(calculateTotalSlots() * 0.5).toFixed(1)} hours = <strong>₹{(formData.customRate * calculateTotalSlots() * 0.5).toLocaleString()}</strong> per booking
                  </p>
                </div>
              )}
            </div>

            {/* Payment Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Payment Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="paymentStatus">Payment Status (Per Booking)</Label>
                  <select
                    id="paymentStatus"
                    className="w-full p-2 border rounded"
                    value={formData.paymentStatus}
                    onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {formData.paymentStatus === 'partial' && (
                  <div>
                    <Label htmlFor="amountPaid">Amount Paid (per booking)</Label>
                    <Input
                      id="amountPaid"
                      type="number"
                      required
                      value={formData.amountPaid}
                      onChange={(e) => setFormData({ ...formData, amountPaid: parseFloat(e.target.value) || 0 })}
                      placeholder="Enter amount paid"
                      max={priceSummary().max}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            {formData.startDate && formData.startTime && formData.endTime && (
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <h3 className="font-semibold">Booking Summary</h3>
                <p className="text-sm">
                  <strong>Court:</strong> {facility.courts?.find(c => c.id === formData.courtId)?.name}
                </p>
                <p className="text-sm" data-testid="bulk-summary-time-range">
                  <strong>Time:</strong> {formatTime(formData.startTime)} → {formatTime(formData.endTime)}
                  {isOvernight() && <span className="text-indigo-700 ml-1">🌙 next day</span>}
                </p>
                <p className="text-sm">
                  <strong>Duration per slot:</strong> {calculateTotalSlots() * 30} minutes ({calculateTotalSlots()} slots)
                </p>
                <p className="text-sm">
                  <strong>Price per booking:</strong> {priceSummary().display}
                  {priceSummary().mixed && <span className="text-[11px] text-amber-700 ml-1">(peak/off-peak or weekend split)</span>}
                  {pricingLoading && <span className="text-[11px] text-gray-500 ml-1">(refreshing…)</span>}
                </p>
                <p className="text-sm">
                  <strong>Total bookings:</strong> {getDatesForRecurrence().length}
                </p>
                <p className="text-sm">
                  <strong>Total amount:</strong> ₹{totalBookingAmount().toFixed(2)}
                </p>
                {pricingError && (
                  <p className="text-xs text-red-600 mt-1">{pricingError}</p>
                )}
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <p className="text-sm font-semibold">Booking Dates:</p>
                  <div className="text-xs mt-1 max-h-20 overflow-y-auto">
                    {getDatesForRecurrence().map((date, idx) => (
                      <span key={idx} className="inline-block bg-blue-100 px-2 py-1 rounded mr-2 mb-1">
                        {new Date(date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Email Notification Options */}
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-sm text-blue-800 flex items-center">
                <Mail className="h-4 w-4 mr-2" />
                Email Notifications
              </h4>
              
              {/* Summary Email (Recommended) */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sendSummaryEmail"
                  checked={formData.sendSummaryEmail}
                  onChange={(e) => setFormData({ ...formData, sendSummaryEmail: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="sendSummaryEmail" className="text-sm cursor-pointer">
                  Send single summary email with all bookings
                  <span className="ml-2 text-xs text-green-600 font-medium">(Recommended)</span>
                </label>
              </div>
              
              {/* Individual Emails (Legacy) */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sendEmailConfirmation"
                  checked={formData.sendEmailConfirmation}
                  onChange={(e) => setFormData({ ...formData, sendEmailConfirmation: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="sendEmailConfirmation" className="text-sm cursor-pointer text-gray-600">
                  Send individual email for each booking
                  <span className="ml-2 text-xs text-gray-500">({getDatesForRecurrence().length} emails)</span>
                </label>
              </div>
              
              {!formData.customerEmail && (formData.sendSummaryEmail || formData.sendEmailConfirmation) && (
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Add customer email above to enable email notifications
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Bookings...</>
              ) : (
                <><Plus className="mr-2 h-4 w-4" /> Create {getDatesForRecurrence().length} Bookings</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Bulk booking result — shown only when there is a MIX of success and failure */}
      <Dialog open={!!failureSummary} onOpenChange={(o) => { if (!o) setFailureSummary(null); }}>
        <DialogContent className="max-w-md" data-testid="bulk-result-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Booking Results</DialogTitle>
          </DialogHeader>
          {failureSummary && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="font-semibold text-green-600" data-testid="bulk-success-count">{failureSummary.successCount} booked</span>
                <span className="text-gray-400"> · </span>
                <span className="font-semibold text-amber-600" data-testid="bulk-skipped-count">{failureSummary.failCount} skipped</span>
                {failureSummary.buckets.summary && (
                  <span className="text-gray-500"> — {failureSummary.buckets.summary}</span>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border border-amber-200 bg-amber-50 divide-y divide-amber-100" data-testid="bulk-failure-list">
                {failureSummary.failures.map((f, i) => (
                  <div key={i} className="px-3 py-2 text-sm" data-testid={`bulk-failure-row-${i}`}>
                    <span className="font-medium text-gray-900">{f.date}</span>
                    <span className="text-red-600"> — {f.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={copyFailureList} data-testid="bulk-copy-failures-btn">
              <Copy className="h-4 w-4 mr-2" /> Copy list
            </Button>
            <Button onClick={() => setFailureSummary(null)} data-testid="bulk-result-close-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkBooking;
